import { ObjectType, Space } from '@fullstory/proto/fs/services/dx-objects';
import { AppDispatch, AppState, SpaceRequestActions, getSavedSpacesById } from '@fullstory/redux';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import { LibraryObjectType } from '@fullstory/routes';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PSEUDO_SPACES } from '../../constants';
import { useActiveSpaceContext } from '../../contexts';
import { getReturnToDashboard } from '../useReturnToDashboard';
import { useDefaultSpace } from './useDefaultSpace';

export type SpaceOwnership = { space: Space | undefined; referencedIn: Space[] };

/**
 * Returns the space owner for the given object type and id.
 * @param objectId object id to search for
 * @param objectType the ObjectType to search for
 * @param shouldFetch whether the hook should dispatch an explicit ownership request
 * @returns Returns the owning space for an object. If enabled is false or if the
 *          object isn't found in our ownership reducer, this returns undefined.
 */
export const useOwningSpaceForObject = (
  objectId: string,
  objectType: LibraryObjectType | null,
  shouldFetch = true,
) => {
  const orgId = useSelector(getOrgId);
  const ownership = useSelector((state: AppState) => state.spaces.ownership);
  const spacesById = useSelector(getSavedSpacesById);
  const dispatch = useDispatch<AppDispatch>();

  const fetch = useCallback(() => {
    dispatch(
      SpaceRequestActions.getObjectSpace({
        orgId,
        dxObjectId: objectId,
        dxObjectType: objectType ?? ObjectType.UNKNOWN,
      }),
    );
  }, [dispatch, objectId, objectType, orgId]);

  useEffect(() => {
    if (objectId && shouldFetch) {
      fetch();
    }
  }, [fetch, objectId, shouldFetch]);

  const spaces: SpaceOwnership | undefined = useMemo(() => {
    if (objectType && ownership[objectType][objectId]) {
      const objectSpaces = ownership[objectType][objectId];
      const space = spacesById[objectSpaces!.space];
      const referencedIn: Space[] = [];
      for (const ref of objectSpaces!.referencedIn) {
        const referencedSpace = spacesById[ref];
        if (referencedSpace) {
          referencedIn.push(referencedSpace);
        }
      }
      return {
        space,
        referencedIn,
      };
    } else {
      return undefined;
    }
  }, [ownership, objectType, objectId, spacesById]);

  return spaces;
};

/**
 * Gets the space for the given object. This is a mixture between active, owning, dashboard and
 * default space. This is determined by:
 * - If the object is in a space, then that space is returned
 * - If the object is new / doesn't have a space then we check if the object is being
 *   viewed/created through a dashboard. The dashboard space is returned
 * - If there is no dashboard space we then look at the active space. If the active space is set to
 *   home or private, then we return the default space to avoid attempts to create in home/private library.
 *
 * @param objectId object id to fetch the space for
 * @param objectType the object type
 * @returns either the owning space, or what should be the owning space upon creation
 */
export const useSpaceForObject = (objectId: string, objectType: LibraryObjectType | null) => {
  const location = useLocation();
  const owningSpace = useOwningSpaceForObject(objectId, objectType, true);

  const { activeSpace } = useActiveSpaceContext();
  const defaultSpace = useDefaultSpace(true);
  const dashboardId = useMemo(() => getReturnToDashboard(location.search), [location.search]);
  const dashboardSpace = useDashboardOwningSpace(dashboardId ?? '');

  return useMemo(() => {
    if (!owningSpace && activeSpace?.space && !PSEUDO_SPACES.includes(activeSpace.space.id)) {
      return {
        space: activeSpace.space,
        referencedIn: [],
      };
    }

    return (
      owningSpace ??
      dashboardSpace ?? {
        space: defaultSpace,
        referencedIn: [],
      }
    );
  }, [activeSpace?.space, dashboardSpace, defaultSpace, owningSpace]);
};

export const useMetricOwningSpace = (objectId: string) =>
  useOwningSpaceForObject(objectId, ObjectType.METRIC, true);

export const useFunnelOwningSpace = (objectId: string) =>
  useOwningSpaceForObject(objectId, ObjectType.FUNNEL, true);

export const useRetentionOwningSpace = (objectId: string) =>
  useOwningSpaceForObject(objectId, ObjectType.RETENTION, true);

export const useSegmentOwningSpace = (objectId: string) =>
  useOwningSpaceForObject(objectId, ObjectType.SEGMENT, true);

export const useDashboardOwningSpace = (objectId: string) =>
  useOwningSpaceForObject(objectId, ObjectType.DASHBOARD, true);

export const useJourneyOwningSpace = (objectId: string) =>
  useOwningSpaceForObject(objectId, ObjectType.JOURNEY, true);

export const useHeatmapOwningSpace = (objectId: string) =>
  useOwningSpaceForObject(objectId, ObjectType.HEATMAP, true);
